import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../lib/api';
import { withCode } from '../../lib/errorCode';
import { useAuth } from '../../lib/auth';
import { useMe } from '../../lib/queries';
import { useTheme, type ThemeChoice } from '../../lib/theme';
import { deleteAccount } from './deleteAccount';
import { AppLanguagePanel, useLanguageSummary, WordsPanel } from './WordsSection';
import { setConsent, useAnalyticsOn } from '../../lib/analytics';
import { useT } from '../../i18n';
import { BackLink } from '../../components/BackLink';

export const APP_VERSION = '0.1.0';

const WIDE = '(min-width: 900px)';
/** True from 900px wide, where the menu stays on screen and picking a section replaces the page in history. */
function useWide() {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(WIDE);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(WIDE).matches,
    () => false,
  );
}

const SECTIONS = ['profile', 'appearance', 'language', 'words', 'privacy', 'account'] as const;
type Section = (typeof SECTIONS)[number];
const isSection = (s: string | undefined): s is Section => SECTIONS.includes(s as Section);

function Chevron() {
  return <span className="settings-chevron" aria-hidden="true" />;
}

/** Name, with the account's email and how it signed in. */
function ProfilePanel() {
  const t = useT();
  const ts = t.settings;
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast((s) => s.show);
  const [name, setName] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: () => api('PATCH', '/me', { displayName: (name ?? '').trim() }),
    onSuccess: () => { toast(t.common.saved); void qc.invalidateQueries(); },
  });
  return (
    <form className="row" onSubmit={(e) => { e.preventDefault(); if ((name ?? '').trim()) rename.mutate(); }}>
      <label className="field grow">
        <span>{ts.yourName}</span>
        <input className="input" value={name ?? me.data?.user.displayName ?? ''} onChange={(e) => setName(e.target.value)} maxLength={80} />
      </label>
      <button className="btn btn-primary" style={{ alignSelf: 'end' }} disabled={!(name ?? '').trim()}>{t.common.save}</button>
    </form>
  );
}

function AppearancePanel() {
  const ts = useT().settings;
  const { theme, set } = useTheme();
  return (
    <div className="segmented" role="group" aria-label={ts.appearance}>
      {(['system', 'light', 'dark'] as ThemeChoice[]).map((th) => (
        <button key={th} type="button" aria-pressed={theme === th} onClick={() => set(th)}>{ts.themes[th]}</button>
      ))}
    </div>
  );
}

/** The usage-statistics switch and the policy links, as rows of one group (PRD 13: opt-out). */
function PrivacyRows() {
  const ts = useT().settings;
  const analyticsOn = useAnalyticsOn();
  const toggle = () => {
    const v = analyticsOn ? 'denied' : 'granted';
    setConsent(v);
    void api('PATCH', '/me', { analyticsConsent: v }).catch(() => {});
  };
  return (
    <div className="settings-group">
      <div className="settings-row">
        <span className="settings-label">
          <span id="share-usage">{ts.shareUsage}</span>
          <small className="muted">{ts.shareUsageHint}</small>
        </span>
        <button type="button" role="switch" className="switch" aria-checked={analyticsOn} aria-labelledby="share-usage" onClick={toggle} />
      </div>
      <Link to="/privacy" className="settings-row"><span className="settings-label">{ts.privacy}</span><Chevron /></Link>
      <Link to="/terms" className="settings-row"><span className="settings-label">{ts.terms}</span><Chevron /></Link>
    </div>
  );
}

function SignOutButton() {
  const ts = useT().settings;
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  return (
    <button type="button" className="settings-row settings-button" onClick={async () => { await signOut(); qc.clear(); void nav({ to: '/' }); }}>
      {ts.signOut}
    </button>
  );
}

/** Sign out, how deletion works, and the typed-confirmation delete flow. */
function AccountPanel() {
  const ts = useT().settings;
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [needPassword, setNeedPassword] = useState(false);
  const email = me.data?.user.email ?? null;
  const expected = email ?? 'DELETE';

  const doDelete = async () => {
    setError('');
    setBusy(true);
    try {
      await deleteAccount({ typed, email, password: needPassword ? password : undefined });
      qc.clear();
      void nav({ to: '/' });
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'needs-password') setNeedPassword(true);
      else if (e instanceof ApiError) setError(e.message);
      else setError(withCode(ts.couldntDelete, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 28 }}>
      <div className="settings-group"><SignOutButton /></div>
      <section className="stack" style={{ gap: 12 }}>
        <h3 className="settings-subtitle">{ts.deleteAccount}</h3>
        {!confirming ? (
          <div className="row" style={{ margin: 0 }}>
            <button type="button" className="btn btn-danger" onClick={() => setConfirming(true)}>{ts.deleteMyAccount}</button>
            <Link to="/delete-account">{ts.howDeletion}</Link>
          </div>
        ) : (
          <form className="stack" onSubmit={(e) => { e.preventDefault(); void doDelete(); }}>
            <p className="muted">{ts.deleteExplainBefore} <strong>{expected}</strong> {ts.deleteExplainAfter}</p>
            <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} aria-label={ts.confirmation} autoComplete="off" />
            {needPassword && (
              <label className="field"><span>{ts.passwordToConfirm}</span>
                <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
            )}
            {error && <div className="error" role="alert">{error}</div>}
            <button className="btn btn-danger" disabled={busy || typed.trim().toLowerCase() !== expected.toLowerCase()}>{ts.deleteForever}</button>
          </form>
        )}
      </section>
    </div>
  );
}

/**
 * Settings (PRD 11.1). On a phone it is a grouped list, and each preference opens its own screen; from 900px
 * wide it is a menu on the left and the chosen section on the right (D-45).
 */
export function Settings({ section: raw }: { section?: string }) {
  const t = useT();
  const ts = t.settings;
  const me = useMe();
  const { user } = useAuth();
  const { theme } = useTheme();
  const summary = useLanguageSummary();
  const wide = useWide();

  const section = isSection(raw) ? raw : undefined;
  const shown: Section = section ?? 'profile';
  const displayName = me.data?.user.displayName ?? user?.displayName ?? '';
  const email = me.data?.user.email ?? null;
  const providerId = user?.providerData[0]?.providerId.replace('.com', '');
  const provider = providerId === 'google' || providerId === 'apple' || providerId === 'password' ? ts.providers[providerId] : null;
  const initial = (displayName || email || '?').trim().charAt(0).toUpperCase();

  const titles: Record<Section, string> = {
    profile: ts.navProfile,
    appearance: ts.appearance,
    language: ts.appLanguage,
    words: ts.words,
    privacy: ts.navPrivacy,
    account: ts.account,
  };
  const panels: Record<Section, ReactNode> = {
    profile: <ProfilePanel />,
    appearance: <AppearancePanel />,
    language: <AppLanguagePanel />,
    words: <WordsPanel />,
    privacy: <PrivacyRows />,
    account: <AccountPanel />,
  };
  const row = (s: Section, value?: string, cls = '') => (
    <Link to="/settings/$section" params={{ section: s }} replace={wide} className={`settings-row ${cls}`} aria-current={section === s ? 'page' : undefined} data-current={shown === s}>
      <span className="settings-label">{titles[s]}</span>
      {value && <span className="settings-value">{value}</span>}
      <Chevron />
    </Link>
  );

  return (
    <div className="page page-wide settings" data-view={section ? 'detail' : 'list'}>
      <nav className="settings-nav" aria-label={ts.title}>
        <div className="row settings-top">
          <BackLink fallback={{ to: '/trees' }} aria-label={t.common.back}>←<span className="wide-only"> {ts.backToTrees}</span></BackLink>
        </div>
        <h1>{ts.title}</h1>

        <Link to="/settings/$section" params={{ section: 'profile' }} replace={wide} className="settings-profile" aria-current={section === 'profile' ? 'page' : undefined} data-current={shown === 'profile'}>
          <span className="avatar" aria-hidden="true">{initial}</span>
          <span className="settings-label">
            <strong>{displayName || ts.navProfile}</strong>
            <small className="muted">{email ?? ts.hiddenEmail}</small>
            {provider && <small className="muted narrow-only">{ts.signedInWith(provider)}</small>}
          </span>
          <Chevron />
        </Link>

        <h2 className="settings-group-title narrow-only">{ts.groupPreferences}</h2>
        <div className="settings-group">
          {row('appearance', ts.themes[theme])}
          {row('language', summary.language)}
          {row('words', summary.words)}
          {row('privacy', undefined, 'wide-only')}
          {row('account', undefined, 'wide-only')}
        </div>

        <div className="narrow-only stack" style={{ gap: 8 }}>
          <h2 className="settings-group-title">{ts.navPrivacy}</h2>
          <PrivacyRows />
          <h2 className="settings-group-title">{ts.account}</h2>
          <div className="settings-group"><SignOutButton /></div>
          <div className="settings-foot">
            <Link to="/settings/$section" params={{ section: 'account' }} className="settings-danger-link">{ts.deleteMyAccount}</Link>
            <Link to="/delete-account" className="muted">{ts.howDeletion}</Link>
          </div>
        </div>
        <p className="muted settings-version">Lineage {APP_VERSION}</p>
      </nav>

      <main className="settings-main">
        <div className="row settings-heading">
          <BackLink fallback={{ to: '/settings' }} className="btn btn-ghost narrow-only" aria-label={t.common.back}>←</BackLink>
          <h2 className="grow">{titles[shown]}</h2>
        </div>
        {shown === 'profile' && (
          <p className="muted" style={{ margin: '0 0 16px' }}>
            {email ?? ts.hiddenEmail}{provider ? ` · ${ts.signedInWith(provider)}` : ''}
          </p>
        )}
        {panels[shown]}
      </main>
    </div>
  );
}
